// @ts-ignore
import { API, FeatureFlagsObject } from 'Core'
import SITE_CONFIG from 'Core/Config'
import { LICENSE_MODES, LICENSE_VIEWS, licenseRouteSegment } from 'Core/Hooks/useLicense/constants'
import {
  getAppViewPreferenceName,
  getLastViewForApp,
  getLicenseMode
} from 'Utils/AuthHelpers'
import { userpreferencesSlice } from 'infra/redux/reducers'
import { enqueueNotification } from 'Utils/Helpers'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector, shallowEqual, useStore } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

export const FeatureFlagContext = React.createContext(null)

export const FeatureFlagProvider = (props) => {
  const [appReady, setAppReady] = useState(false)
  const initiated = useRef(false)
  const { publicUrl } = SITE_CONFIG
  const dispatch = useDispatch()
  const urlPrefix = publicUrl || ''
  // @ts-ignore
  const user = useSelector((state) => state.user)
  // @ts-ignore
  const userpreferences = useSelector((state) => {
    //@ts-ignore
    return Object.values(state.userpreferences.map)
  }, shallowEqual)

  // @ts-ignore
  const history = useHistory()
  // @ts-ignore
  const location = useLocation()
  const store = useStore()
  const [contextValue, setContextValue] = useState({
    featureFlags: {},
    viewMode: 'user',
    licenseMode: LICENSE_MODES.COMBINED,
    loading: true
  })
  /**
   * The one writer for userpreferences attribute changes — the boot
   * LastViewForApp save, the user/admin switch, and the license-mode switch
   * all go through it so they cannot fork in behavior.
   *
   * Hardened against the failure modes observed under load:
   *  - reads preferences from the live Redux store at call time (the
   *    component's selector snapshot can be stale by seconds when the
   *    backend is slow — writing a stale full-object merge was clobbering
   *    a concurrent switch's value, e.g. reverting LicenseMode on reload)
   *  - retries the PUT a few times on failure (silent single-shot failures
   *    left the old mode on the server, so the next boot reverted the user)
   *  - reports failure to the caller instead of swallowing it
   */
  const savePreferenceAttributes = async (change) => {
    const preferenceName = getAppViewPreferenceName(user)
    let lastError = null
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        // fresh read each attempt — never trust a captured snapshot
        const current = Object.values(store.getState().userpreferences.map || {})
        const result = await API.setUserPreferrences(preferenceName, current, change)
        dispatch(userpreferencesSlice.thunk())
        return result
      } catch (error) {
        lastError = error
      }
    }
    console.error('Failed to persist user preference after retries:', change, lastError)
    return null
  }

  const USER_VIEW_ROUTES = new Set([
    'bundles', 'service-accounts', 'devices', 'targets', 'resources',
    'saas-apps', 'iam-roles', 'access-requests', 'policys', 'agentic-apps',
    'addomaincontrollers', 'secrets', 'activity-logs', 'session-logs',
    'passkeys', 'datacloud', 'iam-actions', 'account-resources', 'iam-policy', 'iam-action'
  ])

  const ADMIN_VIEW_ROUTES = new Set([
    'dashboard', 'bundles', 'service-accounts', 'devices', 'targets', 'resources',
    'saas-apps', 'iam-roles', 'access-requests', 'policys', 'agentic-apps',
    'addomaincontrollers', 'secrets', 'activity-logs', 'session-logs',
    'passkeys', 'datacloud', 'iam-actions', 'account-resources', 'iam-policy',
    'users', 'groups', 'gateways', 'proxy-controllers', 'server-controllers',
    'tags', 'clouds', 'integrations', 'settings', 'personal-agents', 'idaanalyzer', 'workloads',
    'agent-config', 'agents'
  ])

  /**
   * Redirects user to the same route in the next view if available, otherwise the view root.
   */
  const redirectToView = (prefferedAppView) => {
    const currentPath = window.location.pathname.toLowerCase()
    let currentSegment = ''
    for (const viewKey of ['admin', 'user']) {
      const prefix = (urlPrefix + '/' + viewKey + '/').toLowerCase()
      if (currentPath.startsWith(prefix)) {
        currentSegment = currentPath.slice(prefix.length).split('/')[0]
        break
      }
    }
    const targetRoutes = prefferedAppView === 'admin' ? ADMIN_VIEW_ROUTES : USER_VIEW_ROUTES
    if (currentSegment && targetRoutes.has(currentSegment)) {
      history.push(`/${prefferedAppView}/${currentSegment}`)
    } else {
      history.push(`/${prefferedAppView}/`)
    }
  }
  /**
   * Runs when user is logged in
   * Checks prefferedAppView and role to select view mode.
   */
  const setRoleBasedPermissions = async () => {
    if (initiated.current) return
    initiated.current = true
    /** Check if url has desired app view mentioned in it */
    const currentUrl = window.location.pathname.toLowerCase()
    const isAdminRouteActive = currentUrl.startsWith(urlPrefix + '/admin')
    const isUserRouteActive = currentUrl.startsWith(urlPrefix + '/user')

    /** Preffered application view based on last session and role type */
    const persistedLastView = getLastViewForApp(userpreferences, user)
    let prefferedAppView = persistedLastView

    /** Override view mode via URL only if user is admin */
    if (user.role === 'admin') {
      if (isUserRouteActive) {
        prefferedAppView = 'user'
      } else if (isAdminRouteActive) {
        prefferedAppView = 'admin'
      }
      /** Save new app view mode to server — only when it actually changed.
       *  The unconditional save this used to be merged the whole attribute
       *  map from possibly-stale Redux state, so under load it could clobber
       *  a concurrent LicenseMode switch write with the stale value. */
      if (persistedLastView !== prefferedAppView) {
        savePreferenceAttributes({ LastViewForApp: prefferedAppView })
      }
    }

    /** Get new set of permissions based on view mode */
    const featureFlags = FeatureFlagsObject.updatePermissionsFor(prefferedAppView)
    const licenseMode = getLicenseMode(userpreferences, user)
    setContextValue((s) => ({
      ...s,
      featureFlags,
      viewMode: prefferedAppView,
      licenseMode,
      loading: false
    }))
    setAppReady(true)
  }

  /**
   * Updates feature flags dynamically from any component.
   * Redirects user to home page of next view
   */
  const updateContext = async (prefferedAppView) => {
    /** Get new set of permissions based on view mode */
    const newFeatureFlags = FeatureFlagsObject.updatePermissionsFor(prefferedAppView)
    setContextValue((s) => ({
      ...s,
      featureFlags: newFeatureFlags,
      viewMode: prefferedAppView,
      loading: false
    }))
    savePreferenceAttributes({ LastViewForApp: prefferedAppView })
    redirectToView(prefferedAppView)
  }

  /**
   * Switches the licensed view (Combined | Agent Privilege | PAM Privilege).
   * Persists the mode to the same server-side preference the app-view switch
   * rides on, then redirects to the Overview dashboard when the page the
   * user is currently on is not part of the newly selected view.
   */
  const licenseSwitched = useRef(false)
  const updateLicenseMode = (licenseMode) => {
    if (!Object.values(LICENSE_MODES).includes(licenseMode)) return
    licenseSwitched.current = true
    setContextValue((s) => ({ ...s, licenseMode }))
    savePreferenceAttributes({ LicenseMode: licenseMode }).then((result) => {
      if (!result) {
        // persist failed even after retries — drop the optimistic value so
        // the next boot does not silently disagree with the server
        enqueueNotification('Could Not Save The View Selection — Please Try Again', 'error')
        setContextValue((s) => ({ ...s, licenseMode: getLicenseMode(Object.values(store.getState().userpreferences.map || {}), user) }))
      }
    })
    const currentPath = window.location.pathname.toLowerCase()
    const adminPrefix = (urlPrefix + '/admin/').toLowerCase()
    if (currentPath.startsWith(adminPrefix)) {
      const segment = licenseRouteSegment(currentPath)
      const view = LICENSE_VIEWS[licenseMode] || {}
      const hidden = view.hiddenRoutes || []
      if (segment && hidden.includes(segment)) {
        // land on the view's home — Overview for agent-side views, the PAM
        // Dashboard for PAM Privilege (where Overview is hidden)
        history.push(view.landingRoute || '/admin/overview')
      }
    }
  }

  /** Boot race guard: effects run child-first, so setRoleBasedPermissions
   *  reads the persisted license mode before the userpreferences thunk has
   *  even been dispatched (the LastViewForApp flow masks this with its URL
   *  override; license mode has no URL). Once the preferences slice arrives,
   *  sync the persisted mode — unless the user switched in this session,
   *  whose optimistic value must win until the server write round-trips. */
  useEffect(() => {
    if (licenseSwitched.current) return
    if (!appReady || !user?.loggedIn) return
    if (!userpreferences.length) return
    const persisted = getLicenseMode(userpreferences, user)
    setContextValue((s) => (s.licenseMode === persisted ? s : { ...s, licenseMode: persisted }))
  }, [userpreferences, appReady, user?.loggedIn])

  const prevRoleRef = useRef(null)

  useEffect(() => {
    if (!user?.loggedIn) return
    const roleChanged = prevRoleRef.current !== null && prevRoleRef.current !== user.role
    prevRoleRef.current = user.role
    if (!initiated.current || roleChanged) {
      initiated.current = false
      setRoleBasedPermissions()
    }
  }, [user.loggedIn, user.role])

  /** Keeps viewMode in sync with the active route. The one-time boot check can
   * compute 'user' from a stale preference while the user lands on (or is
   * redirected to) an /admin route — the header chip then shows User@ on admin
   * pages. Re-derive viewMode from the URL prefix whenever the path changes. */
  useEffect(() => {
    if (!appReady || user?.role !== 'admin') return
    const currentPath = (urlPrefix + location.pathname).toLowerCase()
    if (currentPath.startsWith(urlPrefix + '/admin/')) {
      setContextValue((s) => (s.viewMode === 'admin' ? s : { ...s, viewMode: 'admin' }))
    } else if (currentPath.startsWith(urlPrefix + '/user/')) {
      setContextValue((s) => (s.viewMode === 'user' ? s : { ...s, viewMode: 'user' }))
    }
  }, [location.pathname, appReady, user?.role, urlPrefix])

  return (
    <FeatureFlagContext.Provider
      value={{ data: contextValue, updateContext, updateLicenseMode, appReady }}
    >
      {!appReady && props.Fallback}
      {appReady && props.children}
    </FeatureFlagContext.Provider>
  )
}
